import { inject, Injectable } from '@angular/core';
import { TypDokumentu } from '../../models/typ-dokumentu';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import {
  APPLICATION_CATEGORY,
  ApplicationMainPageInitialData,
} from '../../models/custom-types';
import { Application } from '../../models/application';
import { CheckPossibilityToSubmitDokument } from '../../models/check-possibility-to-submit-dokument';
import { UtworzDokumentDto } from '../../models/utworz-dokument-dto';
import { DefinicjeParametrow } from '../../enums/params-definition.enum';
import { AuthService } from '../../../core/services/auth.service';

@Injectable({ providedIn: 'root' })
export class DokumentApi {
  #httpClient: HttpClient = inject(HttpClient);
  #authService = inject(AuthService);
  readonly #apiUrl = environment.API_EWNIOSKI + '/dokument';

  getMainPageDokumentsList$(): Observable<ApplicationMainPageInitialData> {
    return this.#httpClient
      .get<ApplicationMainPageInitialData>(
        `${this.#apiUrl}/mainPageApplications`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        map(
          (response): ApplicationMainPageInitialData => ({
            // Mapujemy mapę kategorii
            applications: this.#mapApplicationMap(response.applications),

            // Mapujemy listę najczęściej używanych
            mostUsedApplications: this.#mapApplicationList(
              response.mostUsedApplications,
            ),
          }),
        ),
      );
  }

  checkPossibilityToSubmitDokument$(
    kodFormularza: string,
  ): Observable<CheckPossibilityToSubmitDokument> {
    const httpParams = new HttpParams().append('kodFormularza', kodFormularza);
    return this.#httpClient.get<CheckPossibilityToSubmitDokument>(
      `${this.#apiUrl}/checkPossibilityToSubmitDokument`,
      {
        params: httpParams,
        withCredentials: true,
      },
    );
  }

  createNewDokument$(
    utworzDokumentDto: UtworzDokumentDto,
  ): Observable<UtworzDokumentDto> {
    return this.#httpClient.post<UtworzDokumentDto>(
      `${this.#apiUrl}/createNewDokument`,
      utworzDokumentDto,
      {
        withCredentials: true,
      },
    );
  }

  getCorrespondenceDokumentTypesById(id: string): Observable<Application[]> {
    return this.#httpClient.get<Application[]>(
      `${this.#apiUrl}/types/correspondence`,
      {
        params: { id: id },
        withCredentials: true,
      },
    );
  }

  getDokumentTypesCodes(): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#apiUrl}/types/codes`, {
      withCredentials: true,
    });
  }

  getDocumenttypDokumentuMock(
    id: number,
    kod: string,
    name: string,
  ): TypDokumentu {
    return {
      id: id,
      kod: kod,
      nazwa: name,
      czyKorespondencja: null,
      czyMozliwaKorespondencja: null,
      czyKorespondencjaAktywna: null,
      czyNaLiscieNowychDok: null,
      czyUsunietyWSof: null,
      dzienDo: null,
      dzienOd: null,
      grupa: null,
      kodTech: null,
      miesiacDo: null,
      miesiacOd: null,
      odpElektroniczna: null,
      odpTradycyjna: null,
      usuniety: null,
      wyswietlajDlaObszarow: null,
      zmianaObszaru: null,
      zalacznik: null,
    };
  }

  getParamContent$(paramName: DefinicjeParametrow): Observable<string> {
    return this.#httpClient.get(
      `${this.#apiUrl}/pobierzTresc?nazwaParametru=${paramName}`,
      {
        responseType: 'text',
        withCredentials: true,
      },
    );
  }

  #mapApplicationMap(
    rawApplications: Record<string, any[]>,
  ): Record<APPLICATION_CATEGORY, Application[]> {
    return Object.fromEntries(
      Object.entries(rawApplications).map(([key, list]) => [
        key as APPLICATION_CATEGORY,
        list.map((item) => Application.fromDto(item)), // tworzymy instancję
      ]),
    ) as Record<APPLICATION_CATEGORY, Application[]>;
  }

  #mapApplicationList(rawList: any[]): Application[] {
    return (rawList || []).map((item) => Application.fromDto(item));
  }
}
